import { CsvNumberFormat } from '../types/csv.types'

export type { DecimalSeparator, ThousandsSeparator } from '../types/csv.types'

const NUMERIC_ALLOWED = /^[\d., ]+$/
const HAS_DIGIT = /\d/
const THOUSANDS_DOT = /^\d{1,3}(\.\d{3})+$/
const THOUSANDS_COMMA = /^\d{1,3}(,\d{3})+$/
const THOUSANDS_SPACE = /^\d{1,3}( \d{3})+$/
const SPACE_THOUSANDS_DOT_DECIMAL = /^\d{1,3}(?: \d{3})+\.\d+$/
const SPACE_THOUSANDS_COMMA_DECIMAL = /^\d{1,3}(?: \d{3})+,\d+$/

function analyzeBothSeparators(value: string): CsvNumberFormat {
    const lastDot = value.lastIndexOf('.')
    const lastComma = value.lastIndexOf(',')
    return lastDot > lastComma ? { decimalSeparator: '.', thousandsSeparator: ',' } : { decimalSeparator: ',', thousandsSeparator: '.' }
}

function analyzeDotOnly(value: string, dotCount: number, spaceCount: number): CsvNumberFormat | null {
    if (spaceCount > 0) {
        if (dotCount === 1 && SPACE_THOUSANDS_DOT_DECIMAL.test(value)) {
            return { decimalSeparator: '.', thousandsSeparator: ' ' }
        }
        return null
    }
    if (dotCount === 1) {
        if (THOUSANDS_DOT.test(value)) return { decimalSeparator: null, thousandsSeparator: '.' }
        return { decimalSeparator: '.', thousandsSeparator: null }
    }
    if (THOUSANDS_DOT.test(value)) return { decimalSeparator: null, thousandsSeparator: '.' }
    return null
}

function analyzeCommaOnly(value: string, commaCount: number, spaceCount: number): CsvNumberFormat | null {
    if (spaceCount > 0) {
        if (commaCount === 1 && SPACE_THOUSANDS_COMMA_DECIMAL.test(value)) {
            return { decimalSeparator: ',', thousandsSeparator: ' ' }
        }
        return null
    }
    if (commaCount === 1) {
        if (THOUSANDS_COMMA.test(value)) return { decimalSeparator: null, thousandsSeparator: ',' }
        return { decimalSeparator: ',', thousandsSeparator: null }
    }
    if (THOUSANDS_COMMA.test(value)) return { decimalSeparator: null, thousandsSeparator: ',' }
    return null
}

function analyzeValue(rawValue: string): CsvNumberFormat | null {
    const value = rawValue.trim().replace(/^[-+]/, '')
    if (!value) return null
    if (!HAS_DIGIT.test(value)) return null
    if (!NUMERIC_ALLOWED.test(value)) return null

    const dotCount = (value.match(/\./g) ?? []).length
    const commaCount = (value.match(/,/g) ?? []).length
    const spaceCount = (value.match(/ /g) ?? []).length

    if (dotCount === 0 && commaCount === 0 && spaceCount === 0) {
        return { decimalSeparator: null, thousandsSeparator: null }
    }

    if (dotCount > 0 && commaCount > 0) return analyzeBothSeparators(value)
    if (dotCount > 0) return analyzeDotOnly(value, dotCount, spaceCount)
    if (commaCount > 0) return analyzeCommaOnly(value, commaCount, spaceCount)

    if (spaceCount > 0 && THOUSANDS_SPACE.test(value)) {
        return { decimalSeparator: null, thousandsSeparator: ' ' }
    }
    return null
}

// Detects the most likely per-column number format by aggregating per-value
// detections. When no value provides a clear format, returns a plain
// dot-decimal, no-thousands default.
export function detectNumberFormat(sampleValues: readonly string[]): CsvNumberFormat {
    const counts = new Map<string, { fmt: CsvNumberFormat; count: number }>()

    for (const raw of sampleValues) {
        if (raw == null) continue
        const fmt = analyzeValue(raw)
        if (!fmt) continue
        const key = `${fmt.decimalSeparator ?? 'null'}|${fmt.thousandsSeparator ?? 'null'}`
        const existing = counts.get(key)
        if (existing) existing.count++
        else counts.set(key, { fmt, count: 1 })
    }

    let best: CsvNumberFormat = { decimalSeparator: '.', thousandsSeparator: null }
    let bestCount = 0
    for (const { fmt, count } of counts.values()) {
        if (count > bestCount) {
            best = fmt
            bestCount = count
        }
    }
    return best
}
